import { Injectable, OnDestroy } from '@angular/core';
import { AngularFirestore, DocumentChangeAction } from '@angular/fire/firestore';
import { Observable, of, AsyncSubject, combineLatest, fromEvent, Subject } from 'rxjs';
import { map, merge, takeUntil, tap } from 'rxjs/operators';
import { Commessa, OrdineLavoro, RecursiveCommessa, TipoCliente, TipoIntervento, Utente, Autista, Mezzo } from './def/structures';
import { FS_Giorno, FS_Ordine, FS_TipoCliente, FS_TipoIntervento, FS_Autista, FS_Mezzo, FS_Utente } from './def/fs_tables';
import { BatchOperation, BatchOperationUpdate, BatchOperationDelete, BatchOperationSet, BatchOperationAdd } from './def/types';
import { firestore } from 'firebase/app';
import FS_Ref = firestore.DocumentReference;

@Injectable({
    providedIn: 'root'
})
export class DBFirestoreService implements OnDestroy {

    private tipi_cliente$: AsyncSubject<TipoCliente[]>;
    private tipi_intervento$: AsyncSubject<TipoIntervento[]>;
    private utenti$: AsyncSubject<Utente[]>;
    private autisti$: AsyncSubject<Autista[]>;
    private mezzi$: AsyncSubject<Mezzo[]>;

    private unsubscribe$ = new Subject<void>();
    online: boolean = true;

    constructor(
        private db: AngularFirestore
    ) {

        this.isOnline()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(status => {

                if (status === this.online) {
                    return;
                }
                this.online = status;

                if (status) {
                    console.log('Back online');
                    alert('Il collegamento è stato ripristinato. Bentornato.');
                    this.db.firestore.enableNetwork().then(() => {
                        this.db.persistenceEnabled$.subscribe(persistence => {
                            this.db.firestore.waitForPendingWrites().then(() => {
                                if (persistence && confirm('Stai lavorando in modalità OFFLINE/ONLINE. Tornare alla modalità ONLINE per un accesso più veloce?')) {
                                    localStorage.setItem('persistence', 'off');
                                    window.location.href = '/schedule';
                                }
                            });
                        });
                    });

                } else {
                    console.log('Going offline');
                    this.db.firestore.disableNetwork().then(() => {
                        this.db.persistenceEnabled$.subscribe(persistence => {
                            if (!persistence && confirm('Connettività limitata o assente. Vuoi passare alla modalità OFFLINE/ONLINE per continuare a lavorare temporaneamente?')) {
                                localStorage.setItem('persistence', 'on');
                                window.location.href = '/schedule';
                            }
                        });
                    });
                }

            });

        this.initCachedCollections();
    }

    consolelog(...args: any[]): void {
        // console.log(...args);
    }

    initCachedCollections() {
        this.tipi_cliente$ = new AsyncSubject<TipoCliente[]>();
        this.tipi_intervento$ = new AsyncSubject<TipoIntervento[]>();
        this.utenti$ = new AsyncSubject<Utente[]>();
        this.autisti$ = new AsyncSubject<Autista[]>();
        this.mezzi$ = new AsyncSubject<Mezzo[]>();

        const oCached = combineLatest(
            this.collectionTipiCliente(),
            this.collectionTipiIntervento(),
            this.collectionUtenti(),
            this.collectionAutisti(),
            this.collectionMezzi()
        ).subscribe(oCacheresponse => {
            this.consolelog(oCacheresponse);
            // oCached.unsubscribe();
        });
    }

    collectionTipiCliente(returnDisabled: boolean = false): Observable<TipoCliente[]> {
        this.consolelog('collectionTipiCliente');
        return this.db.collection<FS_TipoCliente>('tipi_cliente', ref => {
            return ref.orderBy('peso');
        }).snapshotChanges().pipe(
            map(fsTipiCliente => {
                return fsTipiCliente.map(fsTipoCliente => {
                    const v = fsTipoCliente.payload.doc.data();
                    return {
                        id: fsTipoCliente.payload.doc.id,
                        nome: v.nome,
                        descrizione: v.descrizione,
                        peso: v.peso,
                        disabled: v.disabled,
                    }
                });
            }),
            map(tipiCliente => {
                this.tipi_cliente$.next(tipiCliente);
                this.tipi_cliente$.complete();

                return returnDisabled ? tipiCliente : tipiCliente.filter(tipoCliente => !tipoCliente.disabled);
            }),
            tap(log => this.consolelog(log))
        );
    }

    collectionCachedTipiCliente(returnDisabled: boolean = false): Observable<TipoCliente[]> {
        this.consolelog('collectionCachedTipiCliente');
        return this.tipi_cliente$.pipe(
            map(tipiCliente => {
                return returnDisabled ? tipiCliente : tipiCliente.filter(tipoCliente => !tipoCliente.disabled);
            }),
            tap(log => this.consolelog(log))
        );
    }

    collectionTipiIntervento(returnDisabled: boolean = false): Observable<TipoIntervento[]> {
        this.consolelog('collectionTipiIntervento');
        return this.db.collection<FS_TipoIntervento>('tipi_intervento', ref => {
            return ref.orderBy('peso');
        }).snapshotChanges().pipe(
            map(fsTipiIntervento => {
                return fsTipiIntervento.map(fsTipoIntervento => {
                    const v = fsTipoIntervento.payload.doc.data();
                    return {
                        id: fsTipoIntervento.payload.doc.id,
                        nome: v.nome,
                        descrizione: v.descrizione,
                        modello: v.modello,
                        peso: v.peso,
                        disabled: v.disabled,
                    }
                });
            }),
            map(tipiIntervento => {
                this.tipi_intervento$.next(tipiIntervento);
                this.tipi_intervento$.complete();

                return returnDisabled ? tipiIntervento : tipiIntervento.filter(tipoIntervento => !tipoIntervento.disabled);
            }),
            tap(log => this.consolelog(log))
        );
    }

    collectionCachedTipiIntervento(returnDisabled: boolean = false): Observable<TipoIntervento[]> {
        this.consolelog('collectionCachedTipiIntervento');
        return this.tipi_intervento$.pipe(
            map(tipiIntervento => {
                return returnDisabled ? tipiIntervento : tipiIntervento.filter(tipoIntervento => !tipoIntervento.disabled);
            }),
            tap(log => this.consolelog(log))
        );
    }

    collectionUtenti(returnDisabled: boolean = false): Observable<Utente[]> {
        this.consolelog('collectionUtenti');
        return this.db.collection<FS_Utente>('utenti', ref => {
            return ref.orderBy('cognome');
        }).snapshotChanges().pipe(
            map(fsUtenti => {
                return fsUtenti.map(fsUtente => {
                    const v = fsUtente.payload.doc.data();
                    return {
                        id: fsUtente.payload.doc.id,
                        nome: v.nome,
                        cognome: v.cognome,
                        disabled: v.disabled,
                    }
                });
            }),
            map(utenti => {
                this.utenti$.next(utenti);
                this.utenti$.complete();

                return returnDisabled ? utenti : utenti.filter(utente => !utente.disabled);
            }),
            tap(log => this.consolelog(log))
        );
    }

    collectionCachedUtenti(returnDisabled: boolean = false): Observable<Utente[]> {
        this.consolelog('collectionCachedUtenti');
        return this.utenti$.pipe(
            map(utenti => {
                return returnDisabled ? utenti : utenti.filter(utente => !utente.disabled);
            }),
            tap(log => this.consolelog(log)),
        );
    }

    collectionAutisti(returnDisabled: boolean = false): Observable<Autista[]> {
        this.consolelog('collectionAutisti');
        return this.db.collection<FS_Autista>('autisti', ref => {
            return ref.orderBy('cognome');
        }).snapshotChanges().pipe(
            map(fsAutisti => {
                return fsAutisti.map(fsAutista => {
                    const v = fsAutista.payload.doc.data();
                    return {
                        id: fsAutista.payload.doc.id,
                        nome: v.nome,
                        cognome: v.cognome,
                        disabled: v.disabled,
                    }
                });
            }),
            map(autisti => {
                this.autisti$.next(autisti);
                this.autisti$.complete();

                return returnDisabled ? autisti : autisti.filter(autista => !autista.disabled);
            }),
            tap(log => this.consolelog(log)),
        );
    }

    collectionCachedAutisti(returnDisabled: boolean = false): Observable<Autista[]> {
        this.consolelog('collectionCachedAutisti');
        return this.autisti$.pipe(
            map(autisti => {
                return returnDisabled ? autisti : autisti.filter(autista => !autista.disabled);
            }),
            tap(log => this.consolelog(log)),
        );
    }

    collectionMezzi(returnDisabled: boolean = false): Observable<Mezzo[]> {
        this.consolelog('collectionMezzi');
        return this.db.collection<FS_Mezzo>('mezzi', ref => {
            return ref.orderBy('nome');
        }).snapshotChanges().pipe(
            map(fsMezzi => {
                return fsMezzi.map(fsMezzo => {
                    const v = fsMezzo.payload.doc.data();
                    return {
                        id: fsMezzo.payload.doc.id,
                        nome: v.nome,
                        targa: v.targa,
                        disabled: v.disabled,
                    }
                });
            }),
            map(mezzi => {
                this.mezzi$.next(mezzi);
                this.mezzi$.complete();
                return returnDisabled ? mezzi : mezzi.filter(mezzo => !mezzo.disabled);
            }),
            tap(log => this.consolelog(log)),
        );
    }

    collectionCachedMezzi(returnDisabled: boolean = false): Observable<Mezzo[]> {
        this.consolelog('collectionCachedMezzi');
        return this.mezzi$.pipe(
            map(mezzi => {
                return returnDisabled ? mezzi : mezzi.filter(mezzo => !mezzo.disabled);
            }),
            tap(log => this.consolelog(log)),
        );
    }

    collectionFSOrdini(dateFrom: Date, dateTo: Date): Observable<FS_Ordine[]> {
        this.consolelog('collectionFSOrdini');
        return this.db.collection<FS_Ordine>('ordini', ref => {
            return ref
                .where('data_ricezione', '>=', dateFrom)
                .where('data_ricezione', '<=', dateTo)
                .orderBy('data_ricezione');
        }).snapshotChanges().pipe(
            map(fsOrdini => {
                return fsOrdini.map(fsOrdine => {
                    return fsOrdine.payload.doc.data();
                });
            }),
            tap(log => this.consolelog(log))
        );
    }

    collectionFSGiorni(dateFrom: Date, dateTo: Date): Observable<FS_Giorno[]> {
        this.consolelog('collectionFSGiorni');
        return this.db.collectionGroup<FS_Giorno>('giorni', ref => {
            return ref
                .where('giorno_ora', '>=', dateFrom)
                .where('giorno_ora', '<=', dateTo)
                .orderBy('giorno_ora');
        }).snapshotChanges().pipe(
            map(fsGiorni => {
                return fsGiorni.map(fsGiorno => {
                    return fsGiorno.payload.doc.data();
                });
            }),
            tap(log => this.consolelog(log))
        );
    }

    collectionOrdiniDiLavoro(dateFrom: Date, dateTo: Date): Observable<OrdineLavoro[]> {
        this.consolelog('collectionOrdiniDiLavoro');
        return this.db.collectionGroup<FS_Giorno>('giorni', ref => {
            return ref
                .where('giorno_ora', '>=', dateFrom)
                .where('giorno_ora', '<=', dateTo);

        }).snapshotChanges().pipe(
            map((fsGiorni) => {
                return fsGiorni.map(fsGiorno => {
                    const v = fsGiorno.payload.doc.data();
                    const ordineLavoro = {
                        id: fsGiorno.payload.doc.id,
                        confermato: v.confermato,
                        durata_prevista: v.durata_prevista,
                        giorno_ora: v.giorno_ora ? v.giorno_ora.toDate() : null,
                        indirizzo: v.indirizzo,
                        localita: v.localita,
                        note: v.note,
                        persona_riferimento: v.persona_riferimento,
                        attrezzi: v.attrezzi,

                        autista: null,
                        caposquadra: null,
                        mezzo: null,
                        rif_commessa: fsGiorno.payload.doc.ref.parent.parent.id,

                        telefono: v.telefono,

                        tipo_cliente: v.tipo_cliente,
                        tipo_intervento: v.tipo_intervento,
                    };

                    combineLatest(
                        v.rif_autista ? this.getCachedAutistaById(v.rif_autista.id) : of(null),
                        v.rif_caposquadra ? this.getCachedAutistaById(v.rif_caposquadra.id) : of(null),
                        v.rif_mezzo ? this.getCachedMezzoById(v.rif_mezzo.id) : of(null),

                    ).subscribe(
                        ([autista, caposquadra, mezzo]) => {
                            ordineLavoro.autista = autista;
                            ordineLavoro.caposquadra = caposquadra;
                            ordineLavoro.mezzo = mezzo;
                        });

                    return ordineLavoro;
                });
            }),
            tap(log => this.consolelog(log)),
        );
    }

    collectionOrdiniDiLavoroByParentId(parentId: string): Observable<OrdineLavoro[]> {
        this.consolelog('collectionOrdiniDiLavoroByParentId');
        return this.db.collection<FS_Giorno>('ordini/' + parentId + '/giorni', ref => {
            return ref.orderBy('giorno_ora');
        }).snapshotChanges().pipe(
            map((fsGiorni) => {
                return fsGiorni.map(fsGiorno => {
                    const v = fsGiorno.payload.doc.data();
                    const ordineLavoro = {
                        id: fsGiorno.payload.doc.id,
                        confermato: v.confermato,
                        durata_prevista: v.durata_prevista,
                        giorno_ora: v.giorno_ora ? v.giorno_ora.toDate() : null,
                        indirizzo: v.indirizzo,
                        localita: v.localita,
                        note: v.note,
                        persona_riferimento: v.persona_riferimento,
                        attrezzi: v.attrezzi,

                        autista: null,
                        caposquadra: null,
                        mezzo: null,
                        rif_commessa: parentId,

                        telefono: v.telefono,

                        tipo_cliente: v.tipo_cliente,
                        tipo_intervento: v.tipo_intervento,
                    };

                    combineLatest(
                        v.rif_autista ? this.getCachedAutistaById(v.rif_autista.id) : of(null),
                        v.rif_caposquadra ? this.getCachedAutistaById(v.rif_caposquadra.id) : of(null),
                        v.rif_mezzo ? this.getCachedMezzoById(v.rif_mezzo.id) : of(null),
                    ).subscribe(
                        ([autista, caposquadra, mezzo]) => {
                            ordineLavoro.autista = autista;
                            ordineLavoro.caposquadra = caposquadra;
                            ordineLavoro.mezzo = mezzo;
                        });

                    return ordineLavoro;
                });
            }),
            tap(log => this.consolelog(log)),
        );
    }


    getCommessaById(id: string): Observable<Commessa> {
        this.consolelog('getCommessaById');
        return this.db.doc<FS_Ordine>('ordini/' + id).valueChanges().pipe(
            map((fsOrdine: FS_Ordine) => {
                const v = fsOrdine;
                if (v === undefined) {
                    return null;
                }
                const commessa = {
                    id,
                    cliente: v.cliente,
                    data_ricezione: v.data_ricezione ? v.data_ricezione.toDate() : null,
                    indirizzo: v.indirizzo,
                    localita: v.localita,
                    note: v.note,
                    persona_riferimento: v.persona_riferimento,
                    telefono: v.telefono,

                    numero_ordini: v.numero_ordini,

                    tipo_cliente: null,
                    tipo_intervento: null,
                    utente: null,
                };
                combineLatest(
                    v.tipo_cliente ? this.getCachedTipoClienteById(v.tipo_cliente.id) : of(null),
                    v.tipo_intervento ? this.getCachedTipoInterventoById(v.tipo_intervento.id) : of(null),
                    v.utente ? this.getCachedUtenteById(v.utente.id) : of(null),
                ).subscribe(
                    ([tipo_cliente, tipo_intervento, utente]) => {
                        commessa.tipo_cliente = tipo_cliente;
                        commessa.tipo_intervento = tipo_intervento;
                        commessa.utente = utente;
                    });

                return commessa;
            }),
            tap(log => this.consolelog(log)),
        );
    }

    getRecursiveCommessaById(id: string): Observable<RecursiveCommessa> {
        this.consolelog('getRecursiveCommessaById');
        return new Observable<RecursiveCommessa>(observable => {
            combineLatest(
                this.getCommessaById(id),
                this.collectionOrdiniDiLavoroByParentId(id),
            ).subscribe(([commessa, ordini]) => {
                observable.next({
                    commessa,
                    ordini,
                });
                // observable.complete();
            });
        });
    }


    getTipoClienteById(id: string): Observable<TipoCliente> {
        this.consolelog('getTipoClienteById');
        return this.db.doc<FS_TipoCliente>('tipi_cliente/' + id).valueChanges().pipe(
            map((doc) => {
                return doc ? {
                    id,
                    nome: doc.nome,
                    descrizione: doc.descrizione,
                    peso: doc.peso,
                    disabled: doc.disabled,
                } : null;
            }),
            tap(log => this.consolelog(log)),
        );
    }

    getCachedTipoClienteById(id: string): Observable<TipoCliente> {
        this.consolelog('getCachedTipoClienteById');
        return new Observable<TipoCliente>(subscriber => {
            this.tipi_cliente$.subscribe(tipiCliente => {
                for (const tipoCliente of tipiCliente) {
                    if (tipoCliente.id === id) {
                        subscriber.next(tipoCliente);
                    }
                }
                subscriber.complete();
            });
        });
    }

    getTipoInterventoById(id: string): Observable<TipoIntervento> {
        this.consolelog('getTipoInterventoById');
        return this.db.doc<FS_TipoIntervento>('tipi_intervento/' + id).valueChanges().pipe(
            map((doc) => {
                return doc ? {
                    id,
                    nome: doc.nome,
                    descrizione: doc.descrizione,
                    modello: doc.modello,
                    peso: doc.peso,
                    disabled: doc.disabled,
                } : null;
            })
        );
    }

    getCachedTipoInterventoById(id: string): Observable<TipoIntervento> {
        this.consolelog('getCachedTipoInterventoById');
        return new Observable<TipoIntervento>(subscriber => {
            this.tipi_intervento$.subscribe(tipiIntervento => {
                for (const tipoIntervento of tipiIntervento) {
                    if (tipoIntervento.id === id) {
                        subscriber.next(tipoIntervento);
                    }
                }
                subscriber.complete();
            });
        });
    }

    getUtenteById(id: string): Observable<Utente> {
        this.consolelog('getUtenteById');
        return this.db.doc<FS_Utente>('utenti/' + id).valueChanges().pipe(
            map((doc) => {
                return doc ? {
                    id,
                    nome: doc.nome,
                    cognome: doc.cognome,
                    disabled: doc.disabled,
                } : null;
            }),
            tap(log => this.consolelog(log)),
        );
    }

    getCachedUtenteById(id: string): Observable<Utente> {
        this.consolelog('getCachedUtenteById');
        return new Observable<Utente>(subscriber => {
            this.utenti$.subscribe(utenti => {
                for (const utente of utenti) {
                    if (utente.id === id) {
                        subscriber.next(utente);
                    }
                }
                subscriber.complete();
            });
        });
    }

    getAutistaById(id: string, skip_disabled = false): Observable<Autista> {
        this.consolelog('getAutistaById');
        return this.db.doc<FS_Autista>('autisti/' + id).valueChanges().pipe(
            map((doc) => {
                return doc ? {
                    id,
                    nome: doc.nome,
                    cognome: doc.cognome,
                    disabled: doc.disabled,
                } : null;
            }),
            tap(log => this.consolelog(log)),
        );
    }

    getCachedAutistaById(id: string): Observable<Autista> {
        this.consolelog('getCachedAutistaById');
        return new Observable<Autista>(subscriber => {
            this.autisti$.subscribe(autisti => {
                for (const autista of autisti) {
                    if (autista.id === id) {
                        subscriber.next(autista);
                    }
                }
                subscriber.complete();
            });
        });
    }

    getMezzoById(id: string, skip_disabled = false): Observable<Mezzo> {
        this.consolelog('getMezzoById');
        return this.db.doc<FS_Mezzo>('mezzi/' + id).valueChanges().pipe(
            map((doc) => {
                return doc ? {
                    id,
                    nome: doc.nome,
                    targa: doc.targa,
                    disabled: doc.disabled,
                } : null;
            }),
            tap(log => this.consolelog(log)),
        );
    }

    getCachedMezzoById(id: string): Observable<Mezzo> {
        this.consolelog('getCachedMezzoById');
        return new Observable<Mezzo>(subscriber => {
            this.mezzi$.subscribe(mezzi => {
                for (const mezzo of mezzi) {
                    if (mezzo.id === id) {
                        subscriber.next(mezzo);
                    }
                }
                subscriber.complete();
            });
        });
    }

    getOrdineDiLavoroById(parentId: string, id: string): Observable<OrdineLavoro> {
        this.consolelog('getOrdineDiLavoroById');
        return this.db.doc<FS_Giorno>('ordini/' + parentId + '/ordini/' + id).valueChanges().pipe(
            map((doc) => {
                const v = doc;
                const ordineLavoro = v ? {
                    id,
                    confermato: v.confermato,
                    durata_prevista: v.durata_prevista,
                    giorno_ora: v.giorno_ora ? v.giorno_ora.toDate() : null,
                    indirizzo: v.indirizzo,
                    localita: v.localita,
                    note: v.note,
                    persona_riferimento: v.persona_riferimento,
                    attrezzi: v.attrezzi,

                    autista: null,
                    caposquadra: null,
                    mezzo: null,

                    rif_commessa: parentId,
                    telefono: v.telefono,

                    tipo_cliente: v.tipo_cliente,
                    tipo_intervento: v.tipo_intervento,

                } : null;

                combineLatest(
                    v.rif_autista ? this.getCachedAutistaById(v.rif_autista.id) : of(null),
                    v.rif_caposquadra ? this.getCachedAutistaById(v.rif_caposquadra.id) : of(null),
                    v.rif_mezzo ? this.getCachedMezzoById(v.rif_mezzo.id) : of(null),
                ).subscribe(
                    ([autista, caposquadra, mezzo]) => {
                        ordineLavoro.autista = autista;
                        ordineLavoro.caposquadra = caposquadra;
                        ordineLavoro.mezzo = mezzo;
                    });

                return ordineLavoro;
            }),
            tap(log => this.consolelog(log)),
        );
    }

    getRecursiveCommesse(dateFrom: Date, dateTo: Date): Observable<RecursiveCommessa[]> {
        this.consolelog('getRecursiveCommesse');

        let tmp: { [s: string]: RecursiveCommessa } = {};

        return new Observable(subscriber => {
            this.consolelog('getRecursiveCommesse - fetch from/to', dateFrom, dateTo);
            this.db.collectionGroup<FS_Giorno>('giorni', ref => {
                return ref
                    .orderBy('giorno_ora')
                    .where('giorno_ora', '>', dateFrom)
                    .where('giorno_ora', '<', dateTo);

            }).snapshotChanges().pipe(
                tap(log => this.consolelog('getRecursiveCommesse - FINISH fetch', log)),
                tap(log => this.consolelog('getRecursiveCommesse - Start combine', log)),
                tap((dcaFSGiorni: DocumentChangeAction<FS_Giorno>[]) => {
                    tmp = {};

                    if (!dcaFSGiorni.length) {
                        // this.consolelog('no risultati con ', dateFrom, dateTo);
                        subscriber.next([]);
                        return;
                    }

                    dcaFSGiorni.map(dcaFSGiorno => {

                        const doc = dcaFSGiorno.payload.doc;
                        const parentId = (doc.ref.parent.parent.id);
                        const v = doc.data();

                        if (!tmp[parentId]) {

                            tmp[parentId] = {
                                commessa: null,
                                ordini: [],
                            };
                        }

                        const giorno = {
                            id: doc.id,
                            confermato: v.confermato,
                            durata_prevista: v.durata_prevista,
                            giorno_ora: v.giorno_ora ? v.giorno_ora.toDate() : null,
                            indirizzo: v.indirizzo,
                            localita: v.localita,
                            attrezzi: v.attrezzi,
                            note: v.note,
                            persona_riferimento: v.persona_riferimento,

                            autista: null,
                            caposquadra: null,
                            mezzo: null,

                            rif_commessa: parentId,
                            telefono: v.telefono,

                            tipo_intervento: v.tipo_intervento,
                            tipo_cliente: v.tipo_cliente,
                        };

                        this.consolelog('getRecursiveCommesse - combina autisti/capisquadra/mezzi');
                        combineLatest(
                            v.rif_autista ? this.getCachedAutistaById(v.rif_autista.id) : of(null),
                            v.rif_caposquadra ? this.getCachedAutistaById(v.rif_caposquadra.id) : of(null),
                            v.rif_mezzo ? this.getCachedMezzoById(v.rif_mezzo.id) : of(null),
                        ).pipe(
                            tap(log => this.consolelog('getRecursiveCommesse - FINE combina autisti/capisquadra/mezzi', log)),
                        ).subscribe(
                            ([autista, caposquadra, mezzo]) => {
                                giorno.autista = autista;
                                giorno.caposquadra = caposquadra;
                                giorno.mezzo = mezzo;
                            });

                        tmp[parentId].ordini.push(giorno);

                    });

                    const observables = Object.keys(tmp).map(key => {
                        return this.getCommessaById(key);
                    });

                    this.consolelog('getRecursiveCommesse - combina commesse');
                    combineLatest(observables).pipe(
                        map(docs => {
                            const hashedDocs = {};
                            docs.map(doc => {
                                if (doc && doc.id) {
                                    hashedDocs[doc.id] = doc;
                                }
                            });
                            return hashedDocs;
                        }),

                        tap(log => this.consolelog('getRecursiveCommesse - FINE combina commesse', log))

                    ).subscribe(response => {
                        let idx = 1;
                        subscriber.next(
                            Object.keys(tmp).sort((akey, bkey) => {
                                if (!response[akey] || !response[bkey]) {
                                    return 0;
                                }
                                return response[akey].data_ricezione > response[bkey].data_ricezione ? 1 : -1;
                            }).map(key => {
                                try {
                                    response[key].nordine = idx++;
                                    return {
                                        commessa: response[key],
                                        ordini: tmp[key].ordini,
                                    };
                                } catch (error) {
                                    console.log(key, error)
                                    return {
                                        commessa: null,
                                        ordini: tmp[key].ordini
                                    }
                                }
                            })
                        );
                    });
                }),
                tap(log => this.consolelog('getRecursiveCommesse - finish', log)),

            ).subscribe();

            return {
                unsubscribe() { }
            };
        });

    }

    createId(): string {
        return this.db.createId();
    }

    addCommessa(commessa: Commessa | { data_ricezione: Date }): Promise<FS_Ref> {
        const fsOrdine: FS_Ordine = this.ctype_Commessa2FS_Ordine(commessa as Commessa);
        return this.db.collection<FS_Ordine>('ordini').add(fsOrdine);
    }

    batchAddCommessa(commessa: Commessa): BatchOperationAdd {
        const fsOrdine: FS_Ordine = this.ctype_Commessa2FS_Ordine(commessa);
        return {
            type: 'add',
            ref: this.db.collection('ordini').doc(this.db.createId()).ref,
            data: fsOrdine
        };
    }

    setCommessa(commessa: Commessa): Promise<void> {
        const fsOrdine: FS_Ordine = this.ctype_Commessa2FS_Ordine(commessa);
        return this.db.doc<FS_Ordine>('ordini/' + commessa.id).set(fsOrdine);
    }

    batchSetCommessa(commessa: Commessa): BatchOperationSet {
        const fsOrdine: FS_Ordine = this.ctype_Commessa2FS_Ordine(commessa);
        return {
            type: 'set',
            ref: this.db.doc<FS_Ordine>('ordini/' + commessa.id).ref,
            data: fsOrdine
        }
    }

    updateCommessa(commessa: Partial<Commessa>): Promise<void> {
        const fsOrdine: Partial<FS_Ordine> = this.cptype_Commessa2FS_Ordine(commessa);
        return this.db.doc<FS_Ordine>('ordini/' + commessa.id).update(fsOrdine);
    }

    batchUpdateCommessa(commessa: Partial<Commessa>): BatchOperationUpdate {
        const fsOrdine: Partial<FS_Ordine> = this.cptype_Commessa2FS_Ordine(commessa);
        return {
            type: 'update',
            ref: this.db.doc<FS_Giorno>('ordini/' + commessa.id).ref,
            data: fsOrdine
        }
    }

    deleteCommessa(commessa: { id: string }): Promise<void> {
        return this.db.doc<FS_Ordine>('ordini/' + commessa.id).delete();
    }

    batchDeleteCommessa(commessa: { id: String }): BatchOperationDelete {
        return {
            type: 'delete',
            ref: this.db.doc<FS_Ordine>('ordini/' + commessa.id).ref,
        }
    }

    updateOrdine(ordine: Partial<OrdineLavoro>): Promise<void> {
        const fsGiorno: Partial<FS_Giorno> = this.cptype_OrdineLavoro2FS_Giorno(ordine);
        return this.db.doc<FS_Giorno>('ordini/' + ordine.rif_commessa + '/giorni/' + ordine.id).update(fsGiorno);
    }

    batchUpdateOrdine(ordine: Partial<OrdineLavoro>): BatchOperationUpdate {
        const fsGiorno: Partial<FS_Giorno> = this.cptype_OrdineLavoro2FS_Giorno(ordine);
        return {
            type: 'update',
            ref: this.db.doc<FS_Giorno>('ordini/' + ordine.rif_commessa + '/giorni/' + ordine.id).ref,
            data: fsGiorno
        }
    }

    deleteOrdine(ordine: { id: string, rif_commessa: string }): Promise<void> {
        return this.db.doc<FS_Giorno>('ordini/' + ordine.rif_commessa + '/giorni/' + ordine.id).delete();
    }

    batchDeleteOrdine(ordine: { id: string, rif_commessa: string }): BatchOperationDelete {
        return {
            type: 'delete',
            ref: this.db.doc<FS_Giorno>('ordini/' + ordine.rif_commessa + '/giorni/' + ordine.id).ref,
        }
    }

    setOrdine(ordine: OrdineLavoro): Promise<void> {
        const fsGiorno: FS_Giorno = this.ctype_OrdineLavoro2FS_Giorno(ordine);
        return this.db.doc<FS_Giorno>('ordini/' + ordine.rif_commessa + '/giorni/' + ordine.id).set(fsGiorno);
    }

    batchSetOrdine(ordine: OrdineLavoro): BatchOperationSet {
        const fsGiorno: FS_Giorno = this.ctype_OrdineLavoro2FS_Giorno(ordine);
        return {
            type: 'set',
            ref: this.db.doc<FS_Giorno>('ordini/' + ordine.rif_commessa + '/giorni/' + ordine.id).ref,
            data: fsGiorno
        }
    }

    addOrdine(ordine: OrdineLavoro | { rif_commessa: string, giorno_ora: Date }): Promise<FS_Ref> {
        const fsGiorno: FS_Giorno = this.ctype_OrdineLavoro2FS_Giorno(ordine as OrdineLavoro);
        return this.db.collection<FS_Giorno>('ordini/' + ordine.rif_commessa + '/giorni').add(fsGiorno);
    }

    batchAddOrdine(ordine: OrdineLavoro): BatchOperationAdd {
        const fsGiorno: FS_Giorno = this.ctype_OrdineLavoro2FS_Giorno(ordine);
        return {
            type: 'add',
            ref: this.db.doc('ordini/' + ordine.rif_commessa + '/giorni/' + this.db.createId()).ref,
            data: fsGiorno
        }
    }

    addMezzo(mezzo: Mezzo): Promise<FS_Ref> {
        const fsMezzo: FS_Mezzo = this.ctype_Mezzo2FS_Mezzo(mezzo);
        return this.db.collection<FS_Mezzo>('mezzi').add(fsMezzo);
    }

    batchAddMezzo(mezzo: Mezzo): BatchOperationAdd {
        const fsMezzo: FS_Mezzo = this.ctype_Mezzo2FS_Mezzo(mezzo);
        return {
            type: 'add',
            ref: this.db.doc('mezzi/' + this.db.createId()).ref,
            data: fsMezzo,
        }
    }

    deleteMezzo(mezzo: Mezzo | { id: string }): Promise<void> {
        return this.db.doc<FS_Mezzo>('mezzi/' + mezzo.id).delete();
    }

    batchDeleteMezzo(mezzo: Mezzo | { id: string }): BatchOperationDelete {
        return {
            type: 'delete',
            ref: this.db.doc<FS_Mezzo>('mezzi/' + mezzo.id).ref,
        }
    }

    updateMezzo(mezzo: Partial<Mezzo>): Promise<void> {
        const fsMezzo: Partial<FS_Mezzo> = this.cptype_Mezzo2FS_Mezzo(mezzo);
        return this.db.doc<FS_Mezzo>('mezzi/' + mezzo.id).update(mezzo);
    }

    addAutista(autista: Autista): Promise<FS_Ref> {
        const fsAutista: FS_Autista = this.ctype_Autista2FS_Autista(autista);
        return this.db.collection<FS_Autista>('autisti').add(fsAutista);
    }

    batchAddAutista(autista: Autista): BatchOperationAdd {
        const fsAutista: FS_Autista = this.ctype_Autista2FS_Autista(autista);
        return {
            type: 'add',
            ref: this.db.doc('autisti/' + this.db.createId()).ref,
            data: fsAutista,
        }
    }

    deleteAutista(autista: Autista | { id: string }): Promise<void> {
        return this.db.doc<FS_Autista>('autisti/' + autista.id).delete();
    }

    batchDeleteAutista(autista: Autista | { id: string }): BatchOperationDelete {
        return {
            type: 'delete',
            ref: this.db.doc<FS_Autista>('autisti/' + autista.id).ref,
        }
    }

    updateAutista(autista: Partial<Autista>): Promise<void> {
        const fsAutista: Partial<FS_Autista> = this.cptype_Autista2FS_Autista(autista);
        return this.db.doc<FS_Autista>('autisti/' + autista.id).update(autista);
    }

    addUtente(utente: Utente): Promise<FS_Ref> {
        const fsUtente: FS_Utente = this.ctype_Utente2FS_Utente(utente);
        return this.db.collection<FS_Utente>('utenti').add(fsUtente);
    }

    batchAddUtente(utente: Utente): BatchOperationAdd {
        const fsUtente: FS_Utente = this.ctype_Utente2FS_Utente(utente);
        return {
            type: 'add',
            ref: this.db.doc('utenti/' + this.db.createId()).ref,
            data: fsUtente,
        }
    }

    deleteUtente(utente: Utente | { id: string }): Promise<void> {
        return this.db.doc<FS_Utente>('utenti/' + utente.id).delete();
    }

    batchDeleteUtente(utente: Utente | { id: string }): BatchOperationDelete {
        return {
            type: 'delete',
            ref: this.db.doc<FS_Utente>('utenti/' + utente.id).ref,
        }
    }

    updateUtente(utente: Partial<Utente>): Promise<void> {
        const fsUtente: Partial<FS_Utente> = this.cptype_Utente2FS_Utente(utente);
        return this.db.doc<FS_Utente>('utenti/' + utente.id).update(utente);
    }

    batch(ops: BatchOperation[]) {
        const operation = this.db.firestore.batch();
        ops.forEach(op => {
            switch (op.type) {
                case 'add':
                case 'set':
                    operation.set(op.ref, op.data);
                    break;
                case 'update':
                    operation.update(op.ref, op.data);
                    break;
                case 'delete':
                    operation.delete(op.ref);
                    break;
            }
        });
        return operation.commit();
    }


    // TYPE CONVERSION UTILS

    ctype_OrdineLavoro2FS_Giorno(ordine: OrdineLavoro): FS_Giorno {

        return {
            confermato: ordine.confermato,
            giorno_ora: firestore.Timestamp.fromDate(ordine.giorno_ora),

            durata_prevista: ordine.durata_prevista,

            rif_caposquadra: ordine.caposquadra ? this.db.doc<FS_Autista>('autisti/' + ordine.caposquadra.id).ref : null,
            rif_autista: ordine.autista ? this.db.doc<FS_Autista>('autisti/' + ordine.autista.id).ref : null,
            rif_mezzo: ordine.mezzo ? this.db.doc<FS_Autista>('mezzi/' + ordine.mezzo.id).ref : null,

            attrezzi: ordine.attrezzi,
            indirizzo: ordine.indirizzo,
            localita: ordine.localita,
            persona_riferimento: ordine.persona_riferimento,
            telefono: ordine.telefono,

            note: ordine.note,

            tipo_cliente: ordine.tipo_cliente,
            tipo_intervento: ordine.tipo_intervento,
        }
    }

    // Come sopra, ma restituisce partials
    cptype_OrdineLavoro2FS_Giorno(ordine: Partial<OrdineLavoro>): Partial<FS_Giorno> {
        const retval: Partial<FS_Giorno> = {};

        if ('confermato' in ordine) {
            retval.confermato = ordine.confermato;
        }

        if ('giorno_ora' in ordine) {
            retval.giorno_ora = firestore.Timestamp.fromDate(ordine.giorno_ora);
        }
        if ('durata_prevista' in ordine) {
            retval.durata_prevista = ordine.durata_prevista;
        }

        if ('caposquadra' in ordine) {
            retval.rif_caposquadra = ordine.caposquadra ? this.db.doc<FS_Autista>('autisti/' + ordine.caposquadra.id).ref : null;
        }
        if ('autista' in ordine) {
            retval.rif_autista = ordine.autista ? this.db.doc<FS_Autista>('autisti/' + ordine.autista.id).ref : null;
        }
        if ('mezzo' in ordine) {
            retval.rif_mezzo = ordine.mezzo ? this.db.doc<FS_Autista>('mezzi/' + ordine.mezzo.id).ref : null;
        }

        if ('attrezzi' in ordine) {
            retval.attrezzi = ordine.attrezzi;
        }
        if ('indirizzo' in ordine) {
            retval.indirizzo = ordine.indirizzo;
        }
        if ('localita' in ordine) {
            retval.localita = ordine.localita;
        }
        if ('persona_riferimento' in ordine) {
            retval.persona_riferimento = ordine.persona_riferimento;
        }
        if ('telefono' in ordine) {
            retval.telefono = ordine.telefono;
        }

        if ('note' in ordine) {
            retval.note = ordine.note;
        }

        if ('tipo_cliente' in ordine) {
            retval.tipo_cliente = ordine.tipo_cliente;
        }
        if ('tipo_intervento' in ordine) {
            retval.tipo_intervento = ordine.tipo_intervento;
        }
        return retval;
    }

    ctype_Commessa2FS_Ordine(commessa: Commessa): FS_Ordine {
        return {
            cliente: commessa.cliente,
            data_ricezione: firestore.Timestamp.fromDate(commessa.data_ricezione),
            indirizzo: commessa.indirizzo,
            localita: commessa.localita,
            note: commessa.note,
            persona_riferimento: commessa.persona_riferimento,
            telefono: commessa.telefono,
            numero_ordini: commessa.numero_ordini,
            tipo_cliente: commessa.tipo_cliente ? this.db.doc<FS_TipoCliente>('tipi_cliente/' + commessa.tipo_cliente.id).ref : null,
            tipo_intervento: commessa.tipo_intervento
                ? this.db.doc<FS_TipoIntervento>('tipi_intervento/' + commessa.tipo_intervento.id).ref
                : null,
            utente: commessa.utente ? this.db.doc<FS_Utente>('utenti/' + commessa.utente.id).ref : null,
        }
    }

    cptype_Commessa2FS_Ordine(commessa: Partial<Commessa>): Partial<FS_Ordine> {
        const retval: Partial<FS_Ordine> = {};

        // if ('cliente' in commessa) retval.cliente = commessa.cliente;
        if ('cliente' in commessa && (commessa.cliente !== undefined)) {
            retval.cliente = commessa.cliente;
        }
        if ('data_ricezione' in commessa && (commessa.data_ricezione !== undefined)) {
            retval.data_ricezione = firestore.Timestamp.fromDate(commessa.data_ricezione);
        }
        if ('indirizzo' in commessa && (commessa.indirizzo !== undefined)) {
            retval.indirizzo = commessa.indirizzo;
        }
        if ('localita' in commessa && (commessa.localita !== undefined)) {
            retval.localita = commessa.localita;
        }
        if ('note' in commessa && (commessa.note !== undefined)) {
            retval.note = commessa.note;
        }
        if ('persona_riferimento' in commessa && (commessa.persona_riferimento !== undefined)) {
            retval.persona_riferimento = commessa.persona_riferimento;
        }
        if ('telefono' in commessa && (commessa.telefono !== undefined)) {
            retval.telefono = commessa.telefono;
        }

        if ('numero_ordini' in commessa && (commessa.numero_ordini !== undefined)) {
            retval.numero_ordini = commessa.numero_ordini;
        }

        if ('tipo_cliente' in commessa && (commessa.tipo_cliente !== undefined)) {
            retval.tipo_cliente = commessa.tipo_cliente
                ? this.db.doc<FS_TipoCliente>('tipi_cliente/' + commessa.tipo_cliente.id).ref
                : null;
        }
        if ('tipo_intervento' in commessa && (commessa.tipo_intervento !== undefined)) {
            retval.tipo_intervento = commessa.tipo_intervento
                ? this.db.doc<FS_TipoIntervento>('tipi_intervento/' + commessa.tipo_intervento.id).ref
                : null;
        }
        if ('utente' in commessa && (commessa.utente !== undefined)) {
            retval.utente = commessa.utente ? this.db.doc<FS_Autista>('utenti/' + commessa.utente.id).ref : null;
        }

        return retval;
    }

    ctype_Mezzo2FS_Mezzo(mezzo: Mezzo): FS_Mezzo {
        return {
            nome: mezzo.nome,
            targa: mezzo.targa,
            disabled: mezzo.disabled,
        }
    }

    cptype_Mezzo2FS_Mezzo(mezzo: Partial<Mezzo>): Partial<FS_Mezzo> {
        const retval: Partial<FS_Mezzo> = {};
        if ('nome' in mezzo && (mezzo.nome !== undefined)) {
            retval.nome = mezzo.nome;
        }
        if ('targa' in mezzo && (mezzo.targa !== undefined)) {
            retval.targa = mezzo.targa;
        }
        if ('disabled' in mezzo && (mezzo.disabled !== undefined)) {
            retval.disabled = mezzo.disabled;
        }
        return retval;
    }

    ctype_Autista2FS_Autista(autista: Autista): FS_Autista {
        return {
            nome: autista.nome,
            cognome: autista.cognome,
            disabled: autista.disabled,
        }
    }

    cptype_Autista2FS_Autista(autista: Partial<Autista>): Partial<FS_Autista> {
        const retval: Partial<FS_Autista> = {};
        if ('nome' in autista && (autista.nome !== undefined)) {
            retval.nome = autista.nome;
        }
        if ('cognome' in autista && (autista.cognome !== undefined)) {
            retval.cognome = autista.cognome;
        }
        if ('disabled' in autista && (autista.disabled !== undefined)) {
            retval.disabled = autista.disabled;
        }
        return retval;
    }

    ctype_Utente2FS_Utente(utente: Utente): FS_Utente {
        return {
            nome: utente.nome,
            cognome: utente.cognome,
            disabled: utente.disabled,
        }
    }

    cptype_Utente2FS_Utente(utente: Partial<Utente>): Partial<FS_Utente> {
        const retval: Partial<FS_Autista> = {};
        if ('nome' in utente && (utente.nome !== undefined)) {
            retval.nome = utente.nome;
        }
        if ('cognome' in utente && (utente.cognome !== undefined)) {
            retval.cognome = utente.cognome;
        }
        if ('disabled' in utente && (utente.disabled !== undefined)) {
            retval.disabled = utente.disabled;
        }
        return retval;
    }

    isOnline(): Observable<boolean> {
        return new Observable<boolean>().pipe(
            merge(
                of(navigator.onLine),
                fromEvent(window, 'online').pipe(
                    map(() => true)
                ),
                fromEvent(window, 'offline').pipe(
                    map(() => false)
                ),
            )
        );
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

}
